/* eslint-disable id-length */

'use client'

import { ColorCode, Spacing, Text } from '@vinted/web-ui'
import { useIntl } from 'react-intl'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { parseDateToSeconds } from '@marketplace-web/shared/utils'
import { DailyMetricModel } from '@marketplace-web/vas/vas-data'
import { colorCodeToHex } from '@marketplace-web/vendor-abstractions/web-util'

const Dot = ({
  cx,
  cy,
  stroke,
  strokeWidth,
  value,
}: {
  cx?: number
  cy?: number
  stroke?: string
  strokeWidth?: number
  value?: Array<number>
}) => {
  const breakpoints = useBreakpoint()

  if (!value || typeof value[1] !== 'number') return null

  return (
    <circle
      r={breakpoints.phones ? 4 : 5}
      strokeWidth={strokeWidth}
      stroke={stroke}
      fill={colorCodeToHex(ColorCode.GreyscaleLevel6)}
      cx={cx}
      cy={cy}
    />
  )
}

const Label = ({
  x = 0,
  y = 0,
  payload = { value: '' },
  offset = { horizontal: 0, vertical: 0 },
  alignmentRight,
}: {
  x?: number
  y?: number
  payload?: object & { value: string }
  offset?: {
    horizontal: number
    vertical: number
  }
  alignmentRight?: boolean
}) => {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <foreignObject x={offset.horizontal} y={offset.vertical} width={42} height={16}>
        <Text
          as="span"
          text={payload.value}
          width={Text.Width.Parent}
          alignment={alignmentRight ? Text.Alignment.Right : Text.Alignment.Center}
          type={Text.Type.Caption}
          aria={{ 'aria-hidden': 'true' }}
        />
      </foreignObject>
    </g>
  )
}

type Props = {
  metrics: Array<DailyMetricModel>
  updatedAt: string
}

const CHART_MARGINS = {
  top: Spacing.Regular,
  right: -Spacing.Large,
  left: 0,
  bottom: 0,
}

const ImpressionsAreaChart = ({ metrics, updatedAt }: Props) => {
  const { formatDate } = useIntl()
  const breakpoints = useBreakpoint()

  const updatedAtDateInSeconds = parseDateToSeconds(updatedAt)

  const showZero = (metricDate: string) => {
    if (Number.isNaN(updatedAtDateInSeconds) || Number.isNaN(metricDate)) return undefined

    const metricDateInSeconds = parseDateToSeconds(metricDate)

    return metricDateInSeconds < updatedAtDateInSeconds ? 0 : undefined
  }

  function getTransformedChartData() {
    return metrics.map((metric, index) => {
      const isFirstDayOfTheMonth = new Date(metric.date).getDate() === 1

      const date = formatDate(new Date(metric.date), {
        month: index === 0 || isFirstDayOfTheMonth ? 'short' : undefined,
        day: 'numeric',
      })

      return {
        date,
        promoted: metrics?.[index]?.promoted || showZero(metric.date),
      }
    })
  }

  return (
    <div className="closet-promo-stats__views-chart">
      <ResponsiveContainer aspect={2}>
        <AreaChart data={getTransformedChartData()} margin={CHART_MARGINS}>
          <CartesianGrid
            strokeDasharray="4 4"
            stroke={colorCodeToHex(ColorCode.GreyscaleLevel5)}
            vertical={false}
          />
          <XAxis
            dataKey="date"
            interval={0}
            axisLine={false}
            tickLine={false}
            padding={{ left: 20, right: 20 }}
            tick={<Label offset={{ horizontal: -21, vertical: 7 }} />}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            orientation="right"
            tick={<Label alignmentRight offset={{ horizontal: -8, vertical: -8 }} />}
          />
          <defs>
            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={colorCodeToHex(ColorCode.PrimaryMedium)}
                stopOpacity={0.4}
              />
              <stop
                offset="100%"
                stopColor={colorCodeToHex(ColorCode.GreyscaleLevel6)}
                stopOpacity={0}
              />
            </linearGradient>
          </defs>
          <Area
            type="linear"
            dataKey="promoted"
            stroke={colorCodeToHex(ColorCode.PrimaryDark)}
            strokeWidth={breakpoints.phones ? 2 : 3}
            fillOpacity={1}
            fill="url(#colorUv)"
            dot={<Dot />}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export default ImpressionsAreaChart
